import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_PATHS } from "../../utils/apiPaths";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import moment from "moment";
import { useAuth } from "../../context/AuthContext";

import InputField from "../../components/ui/InputField";
import TextareaField from "../../components/ui/TextareaField";
import SelectField from "../../components/ui/SelectField";
import Button from "../../components/ui/Button";

const CreateInvoice = ({existingInvoice, onSave}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [formData, setFormData] = useState(
    existingInvoice || {
      invoiceNumber: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      billFrom: {
        businessName: user?.businessName || "",
        email: user?.email || "",
        address: user?.address || "",
        phone: user?.phone || "",
      },
      billTo: { clientName: "", email: "", address: "", phone: "" },
      items: [{ name: "", quantity: 1, unitPrice: 0, taxPercent: 0 }],
      notes: "",
      paymentTerms: "Net 15",
    }
  );
  const [loading, setLoading] = useState(false);
  const [isGeneratingNumber, setIsGeneratingNumber] = useState(
    !existingInvoice
  );

  useEffect(() => {
    const aiData = location.state?.aiData;

    if (aiData) {
      setFormData((prev) => ({
        ...prev,
        billTo: {
          clientName: aiData.clientName || "",
          email: aiData.email || "",
          address: aiData.address || "",
          phone: "",
        },
        items: aiData.items || [
          { name: "", quantity: 1, unitPrice: 0, taxPercent: 0 },
        ],
      }));
    }

    if (existingInvoice) {
      setFormData({
        ...existingInvoice,
        invoiceDate: moment(existingInvoice.invoiceDate).format("YYYY-MM-DD"),
        dueDate: moment(existingInvoice.dueDate).format("YYYY-MM-DD"),
      });
    } else {
      const generateNewInvoiceNumber = async () => {
        setIsGeneratingNumber(true);
        try {
          const response = await axiosInstance.get(
            API_PATHS.INVOICE.GET_ALL_INVOICES
          );
          const invoices = response.data;
          let maxNum = 0;
          invoices.forEach((inv) => {
            const num = parseInt(inv.invoiceNumber.split("-")[1]);
            if (!isNaN(num) && num > maxNum) maxNum = num;
          });
          const newInvoiceNumber = `INV-${String(maxNum + 1).padStart(3, "0")}`;
          setFormData((prev) => ({ ...prev, invoiceNumber: newInvoiceNumber }));
        } catch (error) {
          console.error("Failed to generate invoice number", error);
          setFormData((prev) => ({
            ...prev,
            invoiceNumber: `INV-${Date.now().toString().slice(-5)}`,
          }));
        }
        setIsGeneratingNumber(false);
      };
      generateNewInvoiceNumber();
    }
  }, [existingInvoice]);

  const handleInputChange = (e, section, index) => {
    const { name, value } = e.target;
    if (section) {
      setFormData((prev) => ({ ...prev, [section]: { ...prev[section], [name]: value } }));
    } else if (index !== undefined) {
      const newItems = [...formData.items];
      newItems[index] = { ...newItems[index], [name]: value };
      setFormData((prev) => ({ ...prev, items: newItems }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { name: "", quantity: 1, unitPrice: 0, taxPercent: 0 },
      ],
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const { subtotal, taxTotal, total } = (() => {
    let subtotal = 0,
      taxTotal = 0;
    formData.items.forEach((item) => {
      const itemTotal = (item.quantity || 0) * (item.unitPrice || 0);
      subtotal += itemTotal;
      taxTotal += itemTotal * ((item.taxPercent || 0) / 100);
    });
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const itemsWithTotal = formData.items.map((item) => ({
      ...item,
      total: (item.quantity || 0) * (item.unitPrice || 0) * (1 + (item.taxPercent || 0) / 100),
    }));
    const finalFormData = { ...formData, items: itemsWithTotal, subtotal, taxTotal, total };

    if (onSave) {
      await onSave(finalFormData);
    } else {
      try {
        await axiosInstance.post(API_PATHS.INVOICE.CREATE, finalFormData);
        toast.success("¡Orden médica creada exitosamente!");
        navigate("/invoices");
      } catch (error) {
        toast.error("Error al crear la orden médica.");
        console.error(error);
      }
    }
    setLoading(false);
  };

  return <form onSubmit={handleSubmit} className="space-y-8 pb-[100vh]">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-primary-dark">{existingInvoice ? "Editar Orden Médica" : "Crear Orden Médica"}</h2>
        <Button type="submit" isLoading={loading || isGeneratingNumber}>
          {existingInvoice ? "Guardar Cambios" : "Guardar Orden Médica"}
        </Button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm shadow-gray-100 border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InputField
            label="Número de Orden Médica"
            name="invoiceNumber"
            readOnly
            value={formData.invoiceNumber}
            placeholder={isGeneratingNumber ? "Generando..." : ""}
            disabled
          />
          <InputField label="Fecha de Orden Médica" type="date" name="invoiceDate" value={formData.invoiceDate} onChange={handleInputChange} />
          <InputField label="Fecha de Vencimiento" type="date" name="dueDate" value={formData.dueDate} onChange={handleInputChange} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-sm shadow-gray-100 border border-slate-200 space-y-4">
          <h3 className="text-lg font-semibold text-primary-dark mb-2">Emitir Desde</h3>
          <InputField label="Nombre del Negocio" name="businessName" value={formData.billFrom.businessName} onChange={(e) => handleInputChange(e, "billFrom")} />
          <InputField label="Email" type="email" name="email" value={formData.billFrom.email} onChange={(e) => handleInputChange(e, "billFrom")} />
          <TextareaField label="Dirección" name="address" value={formData.billFrom.address} onChange={(e) => handleInputChange(e, "billFrom")} />
          <InputField label="Teléfono" name="phone" value={formData.billFrom.phone} onChange={(e) => handleInputChange(e, "billFrom")} />
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm shadow-gray-100 border border-slate-200 space-y-4">
          <h3 className="text-lg font-semibold text-primary-dark mb-2">Emitir A</h3>
          <InputField label="Nombre del Cliente" name="clientName" value={formData.billTo.clientName} onChange={(e) => handleInputChange(e, "billTo")} />
          <InputField label="Email del Cliente" type="email" name="email" value={formData.billTo.email} onChange={(e) => handleInputChange(e, "billTo")} />
          <TextareaField label="Dirección del Cliente" name="address" value={formData.billTo.address} onChange={(e) => handleInputChange(e, "billTo")} />
          <InputField label="Teléfono del Cliente" name="phone" value={formData.billTo.phone} onChange={(e) => handleInputChange(e, "billTo")} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm shadow-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-slate-200 bg-slate-50">
          <h3 className="text-lg font-semibold text-primary-dark">Artículos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">Artículo</th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">Cant.</th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">Precio</th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">Impuesto (%)</th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">Total</th>
                <th className="px-2 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {formData.items.map((item, index) => (
                <tr key={index} className="hover:bg-slate-50">
                  <td className="px-2 sm:px-6 py-4">
                    <input type="text" name="name" value={item.name} onChange={(e) => handleInputChange(e, null, index)} className="w-full h-10 px-3 py-2 border border-slate-200 rounded-lg bg-white text-primary-dark placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-teal focus:border-transparent" placeholder="Nombre del artículo"/>
                  </td>
                  <td className="px-2 sm:px-6 py-4">
                    <input type="number" name="quantity" value={item.quantity} onChange={(e) => handleInputChange(e, null, index)} className="w-full h-10 px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="1"/>
                  </td>
                  <td className="px-2 sm:px-6 py-4">
                    <input type="number" name="unitPrice" value={item.unitPrice} onChange={(e) => handleInputChange(e, null, index)} className="w-full h-10 px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="0.00"/>
                  </td>
                  <td className="px-2 sm:px-6 py-4">
                    <input type="number" name="taxPercent" value={item.taxPercent} onChange={(e) => handleInputChange(e, null, index)} className="w-full h-10 px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="0"/>
                  </td>
                  <td className="px-2 sm:px-6 py-4 text-sm text-slate-500">${((item.quantity || 0) * (item.unitPrice || 0) * (1 + (item.taxPercent || 0) / 100)).toFixed(2)}</td>
                  <td className="px-2 sm:px-6 py-4">
                    <Button type="button" variant="ghost" size="small" onClick={() => handleRemoveItem(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 sm:p-6 border-t border-slate-200">
          <Button type="button" variant="secondary" onClick={handleAddItem} icon={Plus}>Agregar Artículo</Button>
        </div>
      </div>

       <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-sm shadow-gray-100 border border-slate-200 space-y-4">
          <h3 className="text-lg font-semibold text-primary-dark mb-2">Notas y Términos</h3>
          <TextareaField label="Notas" name="notes" value={formData.notes} onChange={handleInputChange} />
          <SelectField
            label="Términos de Pago"
            name="paymentTerms"
            value={formData.paymentTerms}
            onChange={handleInputChange}
            options={["Net 15", "Net 30", "Net 60", "Due on receipt"]}
          />
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm shadow-gray-100 border border-slate-200 flex flex-col justify-center">
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-primary-dark"><p>Subtotal:</p><p>${subtotal.toFixed(2)}</p></div>
            <div className="flex justify-between text-sm text-primary-dark"><p>Impuesto:</p><p>${taxTotal.toFixed(2)}</p></div>
            <div className="flex justify-between text-lg font-semibold text-primary-dark border-t border-slate-200 pt-4 mt-4"><p>Total:</p><p>${total.toFixed(2)}</p></div>
          </div>
        </div>
      </div>
    </form>
};

export default CreateInvoice;
